import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import AdFacebookPreview from '../../../../features/components/AdFacebookPreview'
import FormDrawerWrapper from '../../../../features/components/FormDrawerWrapper'
import AdFacebookEditSections from './Sections'
import AdPreviewSoundControlWrapper from '../../../../features/components/FacebookPreviews/reusablePreviewComponents/AdPreviewSoundControlWrapper'
import { AdFacebookEditContextProvider } from './AdFacebookEditContextProvider'

import useLoadInstagramAccounts from '../../../../hooks/instagramAccountsHooks/useLoadInstagramAccounts'
import useManageDrawer from '../../../../hooks/formHooks/useManageDrawer'
import useManageEditFormData from '../../../../hooks/formHooks/useManageEditFormData'
import { createJsonFromQueryString } from '../../../../helpers/url'
import { getAdFormat, getPlacementPositions, getVideoIds, transformValuesToFE } from './helpers'
import { getSelectedPlatform } from '../../../../helpers/handlePlatformSelection'
import { useLoadCampaignOnAdApprovalsPage } from './hooks'

import {
  adModifiedDataSelector,
  adSelector,
  adsSelector,
  adUpdateIsLoadingSelector,
  adWasLoadedSelector
} from '../../../../modules/selectors/ads'
import {
  lineItemSelector,
  lineItemsSelector,
  lineItemsWasLoadedSelector,
  lineItemWasLoadedSelector
} from '../../../../modules/selectors/lineItems'
import {
  facebookImageUrlsSelector,
  facebookRequestedVideoIdsSelector,
  facebookVideoUrlsSelector
} from '../../../../modules/selectors/mediaFiles'
import { campaignSelector } from '../../../../modules/selectors/campaigns'
import { choicesCTAByGoalWasLoadedSelector } from '../../../../modules/selectors/choices'
import { selectedFacebookAdAccountSelector } from '../../../../modules/selectors/adAccounts'
import {
  accessibleSocialPagesErrorSelector,
  accessibleSocialPagesWasLoadedSelector
} from '../../../../modules/selectors/socialAccounts'

import { clearBackedInstagramAccounts, clearInstagramAccounts } from '../../../../modules/actions/socialAccounts'
import { clearAd, getAd, setAdCarouselSelectedIndex } from '../../../../modules/actions/ads'
import { clearChoicesCTAByGoal, getChoicesCTAByGoal } from '../../../../modules/actions/choices'
import { getFacebookVideoUrls } from '../../../../modules/actions/mediaFiles'

import { AD_FACEBOOK_EDIT } from '../../../../constants/forms'

import { FB_PAGE_CHOICE } from '../fields'

const AdFacebookEdit = ({ shouldBeCleared, checkPlacements, isAdApprovalsPage }) => {
  const dispatch = useDispatch()

  // if ad is internal, it appears in query params when we open edit form
  const { internal: internalQueryParam } = createJsonFromQueryString(window.location.search)

  const isInternalAd = internalQueryParam === 'true'

  const { objective: campaignObjective } = useSelector(campaignSelector)

  const selectedPlatform = getSelectedPlatform()
  const ads = useSelector(adsSelector)
  const ad = useSelector(adSelector)

  const ctaByGoalWasLoaded = useSelector(choicesCTAByGoalWasLoadedSelector(campaignObjective))

  const adWasLoaded = useSelector(adWasLoadedSelector)
  const adIsUpdating = useSelector(adUpdateIsLoadingSelector)

  const modifiedValues = useSelector(adModifiedDataSelector)

  const lineItems = useSelector(lineItemsSelector)
  const lineItem = useSelector(lineItemSelector)

  const lineItemWasLoaded = useSelector(lineItemWasLoadedSelector)
  const lineItemsWasLoaded = useSelector(lineItemsWasLoadedSelector)
  // if don't need to check placements just set it to true
  const placementPositionsWasLoaded = checkPlacements ? lineItemWasLoaded || lineItemsWasLoaded : true

  const facebookPagesWasLoaded = useSelector(accessibleSocialPagesWasLoadedSelector)
  const facebookPagesError = useSelector(accessibleSocialPagesErrorSelector)

  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)

  const requestedVideoIds = useSelector(facebookRequestedVideoIdsSelector)
  const videoUrls = useSelector(facebookVideoUrlsSelector)
  const imageUrls = useSelector(facebookImageUrlsSelector)

  const placementPositions = useMemo(() => {
    return checkPlacements
      ? getPlacementPositions({ ad, lineItem, lineItems, lineItemWasLoaded, lineItemsWasLoaded })
      : []
  }, [checkPlacements, ad, lineItem, lineItems, lineItemWasLoaded, lineItemsWasLoaded])

  const getAdDataHandler = useCallback(
    selectedEditItemId => {
      const currentAd = ads.find(({ id }) => Number(id) === Number(selectedEditItemId))
      const currentAdAdAccountId = currentAd?.ad_account_id

      const adAccount = currentAdAdAccountId || selectedAdAccountId

      dispatch(
        getAd(
          {
            account: adAccount,
            id: selectedEditItemId,
            ...(isInternalAd && { internal: true })
          },
          selectedPlatform
        )
      )
    },
    [dispatch, selectedAdAccountId, selectedPlatform, isInternalAd, ads]
  )

  const clearAdDataHandler = useCallback(() => {
    if (shouldBeCleared) {
      dispatch(clearAd())
    }

    dispatch(setAdCarouselSelectedIndex('adEditForm', 0))
    dispatch(clearInstagramAccounts())
    dispatch(clearBackedInstagramAccounts())
    dispatch(clearChoicesCTAByGoal())
  }, [dispatch, shouldBeCleared])

  const { isFormOpen } = useManageDrawer({ formName: AD_FACEBOOK_EDIT })

  useManageEditFormData({
    formName: AD_FACEBOOK_EDIT,
    loadedDataId: ad.id,
    getDataHandler: getAdDataHandler,
    clearDataHandler: clearAdDataHandler
  })

  const transformedOriginalValues = useMemo(
    () =>
      transformValuesToFE({
        ad,
        adWasLoaded,
        placementPositions,
        imageUrls,
        videoUrls,
        selectedAdAccountId,
        isEditForm: true
      }),
    [ad, adWasLoaded, placementPositions, imageUrls, videoUrls, selectedAdAccountId]
  )

  const formikValues = useMemo(
    () => ({
      ...transformedOriginalValues,
      ...modifiedValues
    }),
    [transformedOriginalValues, modifiedValues]
  )

  const originalFacebookPage = transformedOriginalValues && transformedOriginalValues[FB_PAGE_CHOICE]
  const adFormat = getAdFormat(transformedOriginalValues)

  // We use this state to track facebook page changes in edit form
  // (we can't use values[FB_PAGE_CHOICE] directly formik is not defined on this level)
  const [selectedFacebookPage, setSelectedFacebookPage] = useState(originalFacebookPage)
  useLoadInstagramAccounts({
    facebookPageId: selectedFacebookPage || originalFacebookPage,
    shouldLoadPageAccessToken: isFormOpen,
    shouldLoadInstagramAccounts: isFormOpen,
    // todo: PBR-998 find out if we need backed instagram accounts
    shouldLoadBackedInstagramAccounts: false
  })

  const notRequestedVideoIds = useMemo(() => {
    // Check if ad has videoIds which were not fetched yet
    // if it has - we need to fetch it to be able to represent video file name.
    if (adWasLoaded) {
      const adVideoIds = getVideoIds(ad)
      return adVideoIds.filter(videoId => !requestedVideoIds.includes(videoId))
    } else {
      return []
    }
  }, [adWasLoaded, ad, requestedVideoIds])

  const isDataLoading = useMemo(() => {
    // check that all needed data for ad previews is loaded and allow pages to be loaded with error
    return (
      !adWasLoaded ||
      !placementPositionsWasLoaded ||
      !(facebookPagesWasLoaded || facebookPagesError) ||
      !!notRequestedVideoIds.length ||
      !ctaByGoalWasLoaded
    )
  }, [
    adWasLoaded,
    placementPositionsWasLoaded,
    facebookPagesWasLoaded,
    facebookPagesError,
    notRequestedVideoIds,
    ctaByGoalWasLoaded
  ])

  // get call-to-action list when form is opened
  useEffect(() => {
    if (isFormOpen && campaignObjective && !ctaByGoalWasLoaded) {
      dispatch(getChoicesCTAByGoal({ goals: campaignObjective }))
    }
  }, [dispatch, campaignObjective, ctaByGoalWasLoaded, isFormOpen])

  useEffect(() => {
    return () => {
      dispatch(clearChoicesCTAByGoal())
    }
  }, [dispatch])

  useEffect(() => {
    // Usually we fetch video url only when user press "play"
    // but in edit form we also need to represent video file name,
    // so we fetch all video info when edit form is opened
    if (notRequestedVideoIds.length) {
      const videoIdsData = [
        {
          account: ad.ad_account_id || ad.creative?.account_id || selectedAdAccountId,
          video_ids: notRequestedVideoIds
        }
      ]

      dispatch(getFacebookVideoUrls(videoIdsData))
    }
  }, [dispatch, ad, notRequestedVideoIds, selectedAdAccountId])

  useEffect(() => {
    if (!isFormOpen) {
      // Clear selected facebook page state when form is closed
      setSelectedFacebookPage(null)
    }
  }, [isFormOpen])

  useLoadCampaignOnAdApprovalsPage({ isAdApprovalsPage, isFormOpen })

  return (
    <FormDrawerWrapper title="Edit a Facebook / Instagram ad" formName={AD_FACEBOOK_EDIT} showOpenButton={false}>
      <AdPreviewSoundControlWrapper formikValues={formikValues} isSoundDisabled={false}>
        <AdFacebookPreview
          originalValues={transformedOriginalValues}
          ad={ad}
          placementPositions={placementPositions}
          isLoading={isDataLoading || adIsUpdating}
          isEditForm
        />
      </AdPreviewSoundControlWrapper>

      <AdFacebookEditContextProvider campaignObjective={campaignObjective}>
        <AdFacebookEditSections
          adFormat={adFormat}
          ad={ad}
          transformedOriginalValues={transformedOriginalValues}
          isDataLoading={isDataLoading}
          setSelectedFacebookPage={setSelectedFacebookPage}
          isInternalAd={isInternalAd}
        />
      </AdFacebookEditContextProvider>
    </FormDrawerWrapper>
  )
}

export default AdFacebookEdit
